import React, { useEffect, useState } from 'react';
import {
  NotificationChannel,
  NotificationPreferencesResponse,
  NotificationPreferenceMatrix,
  notificationsService
} from '../../services/api';
import { disablePush, enablePush, isPushSupported } from '../../services/pushService';

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  in_app: 'In-app',
  push: 'Push',
  email: 'Email',
  sms: 'SMS'
};
const CHANNELS = Object.keys(CHANNEL_LABELS) as NotificationChannel[];

interface Props {
  onSaved?: () => void;
  onCancel?: () => void;
  saveLabel?: string;
}

const NotificationPreferences: React.FC<Props> = ({ onSaved, onCancel, saveLabel = 'Save preferences' }) => {
  const [data, setData] = useState<NotificationPreferencesResponse | null>(null);
  const [prefs, setPrefs] = useState<NotificationPreferenceMatrix>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    notificationsService.getPreferences().then((res) => {
      setData(res);
      setPrefs(res.preferences);
    });
  }, []);

  if (!data) return <p className="text-sm text-gray-500">Loading preferences…</p>;

  const toggle = (category: string, channel: NotificationChannel) =>
    setPrefs((p) => ({ ...p, [category]: { ...p[category], [channel]: !p[category]?.[channel] } }));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const pushWanted = Object.values(prefs).some((c) => c.push);
      let next = prefs;
      if (pushWanted) {
        // Browser permission is requested here, from the user's own click.
        const result = await enablePush();
        if (result !== 'granted') {
          const reason =
            result === 'denied' ? 'Push permission was blocked in your browser.'
            : result === 'unsupported' ? 'Push is not supported on this device.'
            : 'Push is not configured on the server.';
          setMessage(`${reason} Push notifications were turned off.`);
          next = Object.fromEntries(
            Object.entries(prefs).map(([cat, ch]) => [cat, { ...ch, ...(ch.push !== undefined ? { push: false } : {}) }])
          );
          setPrefs(next);
        }
      } else {
        await disablePush();
      }
      await notificationsService.updatePreferences(next);
      onSaved?.();
    } catch (err) {
      setMessage('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-4 font-medium">Notify me about</th>
              {CHANNELS.map((ch) => (
                <th key={ch} className="py-2 px-2 font-medium text-center">{CHANNEL_LABELS[ch]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.categories).map(([category, meta]) => (
              <tr key={category} className="border-t border-gray-100">
                <td className="py-3 pr-4">
                  <div className="font-medium text-gray-900">{meta.label}</div>
                  <div className="text-xs text-gray-500">{meta.description}</div>
                </td>
                {CHANNELS.map((ch) => {
                  const supported = prefs[category] && ch in prefs[category];
                  const disabled = !supported || (ch === 'sms' && !data.hasPhone) || (ch === 'push' && !isPushSupported());
                  return (
                    <td key={ch} className="py-3 px-2 text-center">
                      <input
                        type="checkbox"
                        aria-label={`${meta.label} via ${CHANNEL_LABELS[ch]}`}
                        checked={!!prefs[category]?.[ch]}
                        disabled={disabled}
                        onChange={() => toggle(category, ch)}
                        className="h-4 w-4 accent-blue-600 disabled:opacity-40"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data.hasPhone && (
        <p className="mt-2 text-xs text-gray-500">Add a phone number to your profile to receive SMS.</p>
      )}
      {message && <p className="mt-3 text-sm text-amber-700">{message}</p>}
      <div className="mt-4 flex justify-end gap-2">
        {onCancel && (
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
            Not now
          </button>
        )}
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : saveLabel}
        </button>
      </div>
    </div>
  );
};

export default NotificationPreferences;
